/**
 * Готовые команды строки ⌘K (макет 8-6).
 *
 * Список зависит от открытой страницы: «Что просрочено в проекте DEV» без
 * проекта в адресе — вопрос ни о чём, а «Почему задача висит» на самой задаче
 * уже знает, о какой. Дескриптор для этого и заведён, второго источника
 * «где я стою» у строки нет.
 *
 * Часть команд не отправляется, а дописывается в строку: «Оформить задачу из
 * текста» без самого текста ассистенту слать нечего. Отправлять такую значило
 * бы сжечь обращение из дневного лимита на уточняющий вопрос модели.
 */
import { i18n } from '@/i18n'

import type { PageDescriptor } from './context'

export type PaletteGroup = 'find' | 'create' | 'understand'

/** Значок строки. Компонент разворачивает его в иконку — здесь их нет. */
export type PaletteIcon = 'search' | 'book' | 'plus' | 'folder' | 'clock' | 'board'

export interface PaletteCommand {
  id: string
  group: PaletteGroup
  icon: PaletteIcon
  label: string
  /** Что уедет ассистенту (или встанет в строку, если `fills`). */
  prompt: string
  /** Подпись справа: «база знаний», «черновик», ключ задачи. */
  meta: string
  /** Команду дописывают, а не выполняют: без текста человека она пустая. */
  fills: boolean
}

/** Порядок групп на экране, он же порядок в макете. */
export const PALETTE_GROUPS: PaletteGroup[] = ['find', 'create', 'understand']

export const paletteCommands = (page: PageDescriptor): PaletteCommand[] => {
  const project = page.projectKey ?? ''
  const issue = page.issueKey ?? ''

  const mine = i18n.global.t('palette.commands.mine')

  const overdue = project
    ? i18n.global.t('palette.commands.overdue', { key: project })
    : i18n.global.t('palette.commands.overdueAll')

  const list: PaletteCommand[] = [
    { id: 'mine', group: 'find', icon: 'search', label: mine, prompt: mine, meta: '', fills: false },
    {
      id: 'overdue',
      group: 'find',
      icon: 'search',
      label: overdue,
      prompt: overdue,
      meta: '',
      fills: false,
    },
    {
      id: 'knowledge',
      group: 'find',
      icon: 'book',
      label: i18n.global.t('palette.commands.knowledge'),
      prompt: i18n.global.t('palette.commands.knowledgeFill'),
      meta: i18n.global.t('palette.meta.knowledge'),
      fills: true,
    },
    {
      id: 'compose',
      group: 'create',
      icon: 'plus',
      label: i18n.global.t('palette.commands.compose'),
      prompt: i18n.global.t('palette.commands.composeFill'),
      meta: i18n.global.t('palette.meta.draft'),
      fills: true,
    },
    {
      id: 'decompose',
      group: 'create',
      icon: 'folder',
      label: i18n.global.t('palette.commands.decompose'),
      prompt: i18n.global.t('palette.commands.decomposeFill'),
      meta: i18n.global.t('palette.meta.draft'),
      fills: true,
    },
    // На самой задаче спрашивать не о чем — ключ уже известен. На доске и в
    // списке команда дописывается: какую именно задачу, знает человек.
    {
      id: 'stuck',
      group: 'understand',
      icon: 'clock',
      label: i18n.global.t('palette.commands.stuck'),
      prompt: issue
        ? i18n.global.t('palette.commands.stuckIssue', { key: issue })
        : i18n.global.t('palette.commands.stuckFill'),
      meta: issue,
      fills: !issue,
    },
  ]

  if (project) {
    const digest = i18n.global.t('palette.commands.digest')

    list.push({
      id: 'digest',
      group: 'understand',
      icon: 'board',
      label: digest,
      prompt: digest,
      meta: '',
      fills: false,
    })
  }

  return list
}
